import React, { useEffect } from 'react';

import { makeStyles } from 'tss-react/mui';
import { FieldValues } from 'react-hook-form';
import { Box } from '@mui/material';

import { OhsFormSettings } from 'global-components/form/services/OhsFormModels';
import {
  OhsTaskEndDateRecordField,
  OhsTaskStartDateRecordField,
} from 'task/models/OhsTaskRecordField';

import OhsDateField from './OhsDateField';

interface Props {
  id: string;
  ohsFormSettings: OhsFormSettings<FieldValues>;
  startId?: string;
  endId?: string;
  label?: string;
  maxDate?: {
    name: string;
    date: string;
  };
  isSearch?: boolean;
  required?: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    border: '1px solid lightgrey',
    borderRadius: '4px',
    padding: '8px',
    display: 'flex',

    height: '100%',
    gap: '10px',
  },
  flexColumn: {
    flexDirection: 'column',
  },
  flexRow: {
    flexDirection: 'row',
  },
  fullWidth: {
    width: '100%',
  },
}));

export default function OhsDateRangePickerNew(props: Props) {
  const { classes } = useStyles();
  const rawStartId = props.startId || OhsTaskStartDateRecordField.key;
  const rawEndId = props.endId || OhsTaskEndDateRecordField.key;

  const startId = `${props.id}.${rawStartId}`;
  const endId = `${props.id}.${rawEndId}`;

  const start = props.ohsFormSettings.useFormMethods.watch(startId) ?? null;
  const end = props.ohsFormSettings.useFormMethods.watch(endId) ?? null;
  const endDateState = props.ohsFormSettings.useFormMethods.getFieldState(endId);

  useEffect(() => {
    if (start !== null && end !== null) {
      const startDate: Date = new Date(start);
      const endDate: Date = new Date(end);
      if (startDate > endDate) {
        props.ohsFormSettings.useFormMethods.setError(endId, {
          type: 'notEmpty',
          message: 'End date cannot be before the start date.',
        });
      } else {
        props.ohsFormSettings.useFormMethods.setValue(props.id, {
          [rawStartId]: start,
          [rawEndId]: end,
        });
        props.ohsFormSettings.useFormMethods.trigger();
      }
    }
  }, [start, end, props.id, endDateState?.error?.type]);

  return (
    <>
      {props.label && (
        <Box mb={1} style={{ fontWeight: 'bolder' }}>
          {props.label}
        </Box>
      )}
      <div
        className={`${classes.container} ${props.isSearch ? classes.flexRow : classes.flexColumn}`}
      >
        <Box className={props.isSearch ? classes.fullWidth : ''}>
          <OhsDateField
            title={OhsTaskStartDateRecordField.name}
            id={startId}
            required={props.required || !!end}
            ohsFormSettings={props.ohsFormSettings}
            maxDate={props.maxDate}
          />
        </Box>
        <Box className={props.isSearch ? classes.fullWidth : ''}>
          <OhsDateField
            title={OhsTaskEndDateRecordField.name}
            disabled={!start && !end}
            minDate={{ name: OhsTaskStartDateRecordField.name, date: start }}
            id={endId}
            required={props.required || !!start}
            ohsFormSettings={props.ohsFormSettings}
            maxDate={props.maxDate}
          />
        </Box>
      </div>
    </>
  );
}

OhsDateRangePickerNew.defaultProps = {
  startId: undefined,
  endId: undefined,
  label: '',
  maxDate: undefined,
  isSearch: false,
  required: false,
};
